import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Button,
  Paper,
  Alert,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { StatsAtGlance } from 'components/emailHistory/StatsAtGlance';
import { AIInsight } from 'components/emailHistory/AIInsight';
import { QuickFilters } from 'components/emailHistory/QuickFilters';
import { StatChip } from 'components/emailHistory/StatChip';
import { StatusChip } from 'components/emailHistory/StatusChip';
import { ExpandedRowScheduled } from 'components/emailHistory/ExpandedRowScheduled';
import { EmailCard } from 'components/emailHistory/EmailCard';
import { useEmailHistoryFilters } from 'hooks/emailHistory/useEmailHistoryFilters';
import { useScheduledEmails } from 'hooks/emailHistory/useScheduledEmails';
import { useIsMobile } from 'hooks/useIsMobile';
import { isoToDateInput, dateRangeToISO, formatDateTime, formatDate } from 'utils/emailHistory/dateUtils';
import { DEBOUNCE_DELAY } from 'constants/emailHistory';
import { ROUTE_EMAIL_SENDER } from 'constants/routerPath';


/**
 * Scheduled email row component with inline expansion
 */
function ScheduledEmailRow({ scheduledEmail, expandedRowIds, setExpandedRowIds, campaigns, onRefresh }) {
  const isExpanded = expandedRowIds.includes(scheduledEmail.ScheduleID);

  const toggleExpand = () => {
    if (isExpanded) {
      setExpandedRowIds(expandedRowIds.filter(id => id !== scheduledEmail.ScheduleID));
    } else {
      setExpandedRowIds([...expandedRowIds, scheduledEmail.ScheduleID]);
    }
  };

  // Use ListName from API response, fallback to campaign lookup or default
  const campaignName = scheduledEmail.ListName || 
    (scheduledEmail.ListID === 0 ? 'Individual Email' : 
     (campaigns.find(c => Number(c.campaignId) === Number(scheduledEmail.ListID))?.campaignName || `Campaign ${scheduledEmail.ListID}`));

  // Recipient count from API response
  const recipientCount = scheduledEmail.Recipients ?? (scheduledEmail.ListID === 0 ? 1 : 0);

  return (
    <React.Fragment>
      {/* Main Data Row */}
      <TableRow hover sx={{ cursor: 'pointer' }}>
        <TableCell>
          <IconButton
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            aria-expanded={isExpanded}
            size="small"
            onClick={toggleExpand}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <Typography variant="body1">{scheduledEmail.Subject}</Typography>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <Typography variant="body2">{campaignName}</Typography>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <Typography variant="body2">{formatDateTime(scheduledEmail.ScheduleDate)}</Typography>
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <StatChip value={recipientCount} />
        </TableCell>
        <TableCell onClick={toggleExpand}>
          <StatusChip status={scheduledEmail.Status} type="scheduled" />
        </TableCell>
      </TableRow>

      {/* Expanded Details Row */}
      <TableRow>
        <TableCell sx={{ p: 0, borderBottom: isExpanded ? 1 : 0, bgcolor: 'white' }} colSpan={6}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 3, bgcolor: 'white' }}>
              <ExpandedRowScheduled
                scheduleId={scheduledEmail.ScheduleID}
                onRefresh={onRefresh}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

/**
 * @component ScheduledTab
 * @description Scheduled emails tab showing all scheduled email campaigns
 * @returns {JSX.Element}
 */
function ScheduledTab() {
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { filters, setFilter } = useEmailHistoryFilters();
  const { scheduledEmails, campaigns, stats, isLoading, error, refetch } = useScheduledEmails(filters);
  
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const isMobile = useIsMobile();

  const [dateRange, setDateRange] = useState(() => {
    if (filters.dateFrom || filters.dateTo) {
      return {
        from: isoToDateInput(filters.dateFrom),
        to: isoToDateInput(filters.dateTo),
      };
    }
    return null;
  });

  // Update date range when filters change
  useEffect(() => {
    if (filters.dateFrom || filters.dateTo) {
      setDateRange({
        from: isoToDateInput(filters.dateFrom),
        to: isoToDateInput(filters.dateTo),
      });
    } else {
      setDateRange(null);
    }
  }, [filters.dateFrom, filters.dateTo]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [filters.search, filters.dateFrom, filters.dateTo, filters.campaignId]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilter('search', searchInput);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchInput, setFilter]);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setExpandedRowIds([]); // Close expanded rows when changing pages
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRowIds([]);
  };

  // Handle date range change
  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    const { dateFrom, dateTo } = dateRangeToISO(newRange);
    setFilter('dateFrom', dateFrom);
    setFilter('dateTo', dateTo);
  };

  // Handle campaign filter change
  const handleCampaignChange = (campaignId) => {
    // campaignId comes as a string from Select, or null for "All"
    const parsedId = campaignId && campaignId !== '' ? parseInt(campaignId, 10) : null;
    
    // Update both campaignId and page in a single setSearchParams call to avoid race conditions
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      if (parsedId === null) {
        newParams.delete('campaignId');
      } else {
        newParams.set('campaignId', String(parsedId));
      }
      newParams.set('page', '0');
      return newParams;
    });
  };

  // Handle refresh after updates (reschedule, cancel)
  const handleRefresh = () => {
    // Call the refetch function from the hook to reload data from API
    refetch();
  };

  // Navigate to email sender to send new email
  const handleSendNewEmail = () => {
    navigate(ROUTE_EMAIL_SENDER);
  };

  // Prepare stats for StatsAtGlance using server-side computed stats
  const statsData = [
    {
      label: 'Campaigns Queued',
      value: stats.campaignsQueued,
    },
    {
      label: 'Upcoming Emails',
      value: stats.upcomingEmails,
    },
    {
      label: 'Next Send',
      value: stats.nextSend ? formatDateTime(stats.nextSend) : 'N/A',
      variant: 'h6', // Use smaller font size for date string
    },
    {
      label: 'Recipients Queued',
      value: stats.recipientsQueued,
    },
  ];

  // Campaign dropdown options
  const campaignOptions = [
    { value: '', label: 'All Campaigns' },
    ...campaigns.map(campaign => ({
      value: campaign.campaignId,
      label: campaign.campaignName,
    })),
  ];

  // Generate dynamic title based on filters
  const generateStatsTitle = () => {
    let title = "Viewing All Scheduled Emails";
    const parts = [];

    // Add campaign filter if applied
    if (filters.campaignId) {
      const campaign = campaigns.find(c => c.campaignId === filters.campaignId);
      if (campaign) {
        parts.push(`for ${campaign.campaignName} Campaign`);
      }
    }

    // Add date filters
    if (filters.dateFrom && filters.dateTo) {
      parts.push(`Between ${formatDate(filters.dateFrom)} and ${formatDate(filters.dateTo)}`);
    } else if (filters.dateFrom) {
      parts.push(`from ${formatDate(filters.dateFrom)}`);
    } else if (filters.dateTo) {
      parts.push(`Up To ${formatDate(filters.dateTo)}`);
    }

    if (parts.length > 0) {
      title += ` ${parts.join(' ')}`;
    }

    return title;
  };

  const statsTitle = generateStatsTitle();
  const statsSubtitle = "A Scheduled Email is a bulk email which will be sent automatically at a future date and time.";

  return (
    <Box>
      {/* Stats + AI Overview Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <StatsAtGlance stats={statsData} loading={isLoading} title={statsTitle} subtitle={statsSubtitle} />
        </Grid>
        <Grid item xs={12} md={4}>
          <AIInsight tab="scheduled" filters={filters} />
        </Grid>
      </Grid>

      {/* Quick Filters and Data Table */}
      <Paper>
        <QuickFilters
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Search subject..."
          showDateRange={true}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          dateRangeDefault="all"
          startButton={
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleSendNewEmail}
              fullWidth={isMobile}
            >
              Send new email
            </Button>
          }
          additionalFilter={{
            label: 'Campaign',
            value: filters.campaignId != null ? filters.campaignId : '',
            options: campaignOptions,
            onChange: handleCampaignChange,
          }}
        />

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {isMobile ? (
          <Box sx={{ p: 2 }}>
            {isLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : scheduledEmails.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No scheduled emails
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You don't have any emails scheduled. Schedule a campaign to see it here.
                </Typography>
              </Box>
            ) : (
              <>
                {scheduledEmails
                  .slice(page * pageSize, page * pageSize + pageSize)
                  .map((scheduledEmail) => {
                    const isExpanded = expandedRowIds.includes(scheduledEmail.ScheduleID);
                    const campaignName = scheduledEmail.ListName || 
                      (scheduledEmail.ListID === 0 ? 'Individual Email' : 
                       (campaigns.find(c => Number(c.campaignId) === Number(scheduledEmail.ListID))?.campaignName || `Campaign ${scheduledEmail.ListID}`));
                    const recipientCount = scheduledEmail.Recipients ?? (scheduledEmail.ListID === 0 ? 1 : 0);

                    return (
                      <EmailCard
                        key={scheduledEmail.ScheduleID}
                        data={{
                          Subject: scheduledEmail.Subject,
                          ListName: campaignName,
                          ScheduleDate: formatDateTime(scheduledEmail.ScheduleDate),
                          contact_count: recipientCount,
                          Status: scheduledEmail.Status,
                          ListID: scheduledEmail.ListID,
                        }}
                        type="scheduled"
                        isExpanded={isExpanded}
                        onToggleExpand={() => {
                          if (isExpanded) {
                            setExpandedRowIds(expandedRowIds.filter(id => id !== scheduledEmail.ScheduleID));
                          } else {
                            setExpandedRowIds([...expandedRowIds, scheduledEmail.ScheduleID]);
                          }
                        }}
                        expandedContent={
                          isExpanded ? (
                            <ExpandedRowScheduled
                              scheduleId={scheduledEmail.ScheduleID}
                              onRefresh={handleRefresh}
                            />
                          ) : null
                        }
                      />
                    );
                  })}
                <TablePagination
                  component="div"
                  count={scheduledEmails.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[]}
                />
              </>
            )}
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={50} />
                    <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Campaign</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Scheduled Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Recipients</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : scheduledEmails.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No scheduled emails
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          You don't have any emails scheduled. Schedule a campaign to see it here.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    // Client-side pagination: slice the data for the current page
                    scheduledEmails
                      .slice(page * pageSize, page * pageSize + pageSize)
                      .map((scheduledEmail) => (
                        <ScheduledEmailRow
                          key={scheduledEmail.ScheduleID}
                          scheduledEmail={scheduledEmail}
                          expandedRowIds={expandedRowIds}
                          setExpandedRowIds={setExpandedRowIds}
                          campaigns={campaigns}
                          onRefresh={handleRefresh}
                        />
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={scheduledEmails.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

export default ScheduledTab;
